import { useCallback, useRef, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { length } from 'stringz';

import { showAlert } from 'mastodon/actions/alerts';
import { reblog } from 'mastodon/actions/interactions_typed';
import AutosuggestTextarea from 'mastodon/components/autosuggest_textarea';
import { Avatar } from 'mastodon/components/avatar';
import { Button } from 'mastodon/components/button';
import { DisplayName } from 'mastodon/components/display_name';
import { IconButton } from 'mastodon/components/icon_button';
import { CloseIcon } from 'mastodon/components/icons/close';
import { CharacterCounter } from 'mastodon/features/compose/components/character_counter';
import PrivacyDropdown from 'mastodon/features/compose/components/privacy_dropdown';
import EmojiPickerDropdown from 'mastodon/features/compose/containers/emoji_picker_dropdown_container';
import { me } from 'mastodon/initial_state';
import { useAppDispatch, useAppSelector } from 'mastodon/store';

const messages = defineMessages({
  placeholder: {
    id: 'share_modal.placeholder',
    defaultMessage: 'Say something about this...',
  },
  shareNow: {
    id: 'share_modal.share_now',
    defaultMessage: 'Share now',
  },
  shared: {
    id: 'share_modal.shared',
    defaultMessage: 'Shared',
  },
});

const noop = () => {};

export const QuoteZone = ({ status, onClose }) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const textareaRef = useRef(null);
  const maxChars = 500;

  const currentAccount = useAppSelector(state => (me ? state.accounts.get(me) : undefined));
  const defaultPrivacy = useAppSelector(state => state.compose.get('default_privacy'));
  const statusVisibility = status?.get('visibility');
  const [privacy, setPrivacy] = useState(statusVisibility === 'private' ? 'private' : defaultPrivacy);
  const [caption, setCaption] = useState('');

  const handleChange = useCallback(value => {
    const nextValue = typeof value === 'string' ? value : value.target.value;

    if (length(nextValue) <= maxChars) {
      setCaption(nextValue);
    }
  }, []);

  const handleEmojiPick = useCallback(data => {
    const textarea = textareaRef.current;
    const emojiText = data.native ?? data.colons ?? `:${data.id}:`;
    const position = textarea?.selectionStart ?? caption.length;
    const nextCaption = caption.slice(0, position) + emojiText + caption.slice(position);

    if (length(nextCaption) <= maxChars) {
      setCaption(nextCaption);
      requestAnimationFrame(() => {
        textarea?.focus();
        textarea?.setSelectionRange(position + emojiText.length, position + emojiText.length);
      });
    }
  }, [caption]);

  const handleSubmit = useCallback(() => {
    if (!status || length(caption) > maxChars) {
      return;
    }

    dispatch(reblog({
      statusId: status.get('id'),
      visibility: privacy,
      reblog_caption: caption.trim() || undefined,
    }));
    dispatch(showAlert({ message: messages.shared }));
    onClose?.();
  }, [caption, dispatch, onClose, privacy, status]);

  if (!status) {
    return null;
  }

  return (
    <section className='share-modal__zone share-modal__quote-zone'>
      <div className='share-modal__quote-card'>
        <div className='share-modal__identity'>
          <Avatar account={currentAccount} size={56} />
          <div className='share-modal__identity-main'>
            <DisplayName account={currentAccount} />
            {!status.get('reblogged') && (
              <PrivacyDropdown
                noDirect
                value={privacy}
                container={document.body}
                onChange={setPrivacy}
                disabled={statusVisibility === 'private'}
              />
            )}
          </div>
          <IconButton
            className='share-modal__close'
            title='Close'
            icon='times'
            iconComponent={CloseIcon}
            onClick={onClose}
          />
        </div>

        <div className='share-modal__textarea'>
          <AutosuggestTextarea
            ref={textareaRef}
            placeholder={intl.formatMessage(messages.placeholder)}
            value={caption}
            onChange={handleChange}
            suggestions={[]}
            onSuggestionsFetchRequested={noop}
            onSuggestionsClearRequested={noop}
            onSuggestionSelected={noop}
            autoFocus={false}
            lang='vi'
            minRows={8}
            maxRows={12}
          />
        </div>

        <div className='share-modal__quote-actions'>
          <div className='share-modal__quote-tools'>
            <EmojiPickerDropdown isComposeForm onPickEmoji={handleEmojiPick} />
            <CharacterCounter max={maxChars} text={caption} />
          </div>
          <Button
            onClick={handleSubmit}
            text={intl.formatMessage(messages.shareNow)}
            disabled={length(caption) > maxChars}
          />
        </div>
      </div>
    </section>
  );
};
